import type { Content, Element, ItemDef, Stats } from '../data/schema';
import { equipItem } from './equipment';
import { ZERO_META_BONUSES, type MetaBonuses } from './meta/bonuses';
import { applyCard, effectiveCooldown, type LevelUpCard } from './progression';
import { effectiveDamage } from './systems/spells';
import { STAT_FOR_ELEMENT, type Player } from './types';
import type { World } from './world';

/**
 * Stats « roguelike » dérivées des caractéristiques Dofus et du sort principal (lecture façon Isaac).
 * Chaque multiplicateur vaut 1 pour la classe de base sans carte ni objet ; la valeur brute l'accompagne.
 */
export interface DerivedStats {
  /** Vitesse d'attaque : recharge de base / recharge effective du sort principal, et tirs par seconde. */
  attackSpeed: { mult: number; shotsPerSec: number };
  /** Dégâts d'un projectile (ou d'une zone) du sort principal, bonus élémentaire compris ; `mult` par rapport aux dégâts bruts du sort. */
  damage: { element: Element; min: number; max: number; mult: number } | null;
  /** Vitesse des projectiles (px/s) ; null si le sort principal n'en tire pas. */
  projectileSpeed: { mult: number; pxPerSec: number } | null;
  /** Vitesse de déplacement : 1 + speedPct/100, et px/s. */
  moveSpeed: { mult: number; pxPerSec: number };
  /** Portée : durée de vie x vitesse d'un projectile, ou portée maximale d'une zone. */
  range: { mult: number; px: number } | null;
  /** Projectiles par tir et perforation ; null pour un sort sans projectile. */
  projectiles: { count: number; pierce: number } | null;
  hpMax: number;
  /** Gain d'XP : 1 + Sagesse/100. */
  xpMult: number;
}

export type DerivedKey = 'attackSpeed' | 'damage' | 'projectileSpeed' | 'moveSpeed' | 'range' | 'projectiles' | 'hpMax' | 'xpMult';
/** Variation d'une stat dérivée : en pourcentage (multiplicateurs) ou en valeur (projectiles, PV max). */
export type DerivedChange = { key: DerivedKey; pct: number } | { key: DerivedKey; delta: number };

/** Effet réel d'une caractéristique Dofus dans le coeur : bonus élémentaire, XP ou PV max. */
export type StatEffect = { kind: 'element'; element: Element; pct: number } | { kind: 'xp'; pct: number } | { kind: 'hp'; amount: number };

const round1 = (x: number): number => Math.round(x * 10) / 10;

/**
 * `bonusPct` : bonus de dégâts du moment (buffs actifs, Souffrance du Sacrieur), comme `playerDamagePct` en combat.
 * `meta` : bonus de méta de la run (recharge, dégâts et XP), appliqués comme en combat ; sans méta, valeurs de base.
 */
export function derivedStats(p: Player, content: Content, bonusPct = 0, meta: MetaBonuses = ZERO_META_BONUSES): DerivedStats {
  const cls = content.classes[p.defId];
  const spell = content.spells[p.primary.id];
  const moveMult = 1 + p.speedPct / 100;
  const out: DerivedStats = {
    attackSpeed: { mult: 1, shotsPerSec: 0 },
    damage: null, projectileSpeed: null,
    moveSpeed: { mult: moveMult, pxPerSec: (cls?.speed ?? 200) * moveMult },
    range: null, projectiles: null,
    hpMax: p.hpMax,
    xpMult: (1 + meta.xpPct / 100) * (1 + p.stats.sagesse / 100),
  };
  if (!spell) return out;
  const cd = effectiveCooldown(spell, p.primary, meta.cooldownPct);
  out.attackSpeed = { mult: spell.cooldown / cd, shotsPerSec: 1 / cd };
  const dmg = effectiveDamage(spell, p.primary, p.stats, bonusPct, meta.damagePct);
  if (dmg) {
    const statKey = STAT_FOR_ELEMENT[dmg.element];
    const stat = statKey ? p.stats[statKey] : 0;
    out.damage = { ...dmg, mult: (1 + (p.primary.mods.damagePct + bonusPct) / 100) * (100 + stat) / 100 * (1 + meta.damagePct / 100) };
  }
  const fx = spell.effects.find((e) => e.type === 'projectile' || e.type === 'zone' || e.type === 'melee');
  if (fx?.type === 'projectile') {
    out.projectileSpeed = { mult: 1, pxPerSec: fx.speed };
    out.range = { mult: 1, px: round1(fx.speed * fx.lifetime) };
    out.projectiles = { count: fx.count + p.primary.mods.countBonus, pierce: fx.pierce };
  } else if (fx?.type === 'zone') {
    const radiusMult = 1 + p.primary.mods.radiusPct / 100;
    out.range = fx.at === 'aim' ? { mult: 1, px: fx.maxRange } : { mult: radiusMult, px: round1(fx.radius * radiusMult) };
  } else if (fx?.type === 'melee') {
    // Coup en cône : la portée grandit avec radiusPct, pas de projectile.
    const radiusMult = 1 + p.primary.mods.radiusPct / 100;
    out.range = { mult: radiusMult, px: round1(fx.range * radiusMult) };
  }
  return out;
}

export function statEffect(stat: keyof Stats, value: number): StatEffect {
  if (stat === 'vitalite') return { kind: 'hp', amount: value };
  if (stat === 'sagesse') return { kind: 'xp', pct: value };
  const element = (Object.keys(STAT_FOR_ELEMENT) as Element[]).find((el) => STAT_FOR_ELEMENT[el] === stat)!;
  return { kind: 'element', element, pct: value };
}

const pctChange = (before: number, after: number): number => (before > 0 ? round1((after / before - 1) * 100) : 0);

/** Différences entre deux jeux de stats dérivées, dans l'ordre d'affichage ; vide si rien ne change. */
export function derivedChanges(before: DerivedStats, after: DerivedStats): DerivedChange[] {
  const out: DerivedChange[] = [];
  const pct = (key: DerivedKey, a: number, b: number): void => { const v = pctChange(a, b); if (v !== 0) out.push({ key, pct: v }); };
  const delta = (key: DerivedKey, a: number, b: number): void => { if (b !== a) out.push({ key, delta: b - a }); };
  pct('attackSpeed', before.attackSpeed.mult, after.attackSpeed.mult);
  if (before.damage && after.damage) pct('damage', before.damage.mult, after.damage.mult);
  if (before.projectileSpeed && after.projectileSpeed) pct('projectileSpeed', before.projectileSpeed.mult, after.projectileSpeed.mult);
  pct('moveSpeed', before.moveSpeed.mult, after.moveSpeed.mult);
  if (before.range && after.range) pct('range', before.range.mult, after.range.mult);
  if (before.projectiles && after.projectiles) delta('projectiles', before.projectiles.count, after.projectiles.count);
  delta('hpMax', before.hpMax, after.hpMax);
  pct('xpMult', before.xpMult, after.xpMult);
  return out;
}

/** Monde minimal pour appliquer une carte ou un objet sur une copie du joueur : `applyCard` et `equipItem` ne lisent que `content`, `events`, `time` (`recomputeStats` garde les buffs actifs) et `meta` (PV max de méta). */
function previewWorld(content: Content, meta: MetaBonuses): World { return { content, events: [], time: 0, meta } as unknown as World; }
function clonePlayer(p: Player): Player { return structuredClone(p); }

/** Effet d'une carte de level-up sur les stats dérivées, sans modifier le joueur. */
export function cardChanges(content: Content, p: Player, card: LevelUpCard, meta: MetaBonuses = ZERO_META_BONUSES): DerivedChange[] {
  const clone = clonePlayer(p);
  applyCard(previewWorld(content, meta), clone, card);
  return derivedChanges(derivedStats(p, content, 0, meta), derivedStats(clone, content, 0, meta));
}

/** Effet d'un objet (équipé à la place de l'actuel) sur les stats dérivées, sans modifier le joueur. */
export function itemChanges(content: Content, p: Player, item: ItemDef, meta: MetaBonuses = ZERO_META_BONUSES): DerivedChange[] {
  const clone = clonePlayer(p);
  equipItem(previewWorld(content, meta), clone, item);
  return derivedChanges(derivedStats(p, content, 0, meta), derivedStats(clone, content, 0, meta));
}
